/**
 * Типограф для текста, пришедшего с сервера.
 *
 * Свои подписи мы расставляем руками по `.claude/rules/typography.md`, но
 * половина отчёта — объяснения, вердикты и формулировки задач, которые
 * пишет генератор. Руками их не тронуть, а без служебных символов документ
 * рвётся ровно там, где читать больнее всего: предлог остаётся на одной
 * строке, его слово уходит на другую, сумма разваливается по разрядам,
 * «Санкт-Петербург» переносится по дефису.
 *
 * Правила — те же, что и для ручного набора:
 *
 *   1. число не рвётся по разрядам и не отрывается от единицы;
 *   2. предлог, союз и вообще короткое слово липнут к следующему;
 *   3. составное слово не переносится по дефису;
 *   4. длинное тире не начинает строку;
 *   5. последнее слово абзаца не висит одно.
 *
 * @format
 */

import type {ReportData} from './types';

/** Неразрывный пробел: по нему строка не переносится. */
const NBSP = '\u00A0';

/**
 * Предельная длина связки последних двух слов, в знаках.
 *
 * Связка длиннее не помещается в узкую колонку и вместо спасения от висячего
 * слова даёт переполнение вбок.
 */
const LAST_PAIR_MAX = 16;

/** Word joiner — невидимый «клей»: запрещает перенос, ничего не рисуя. */
const WJ = '\u2060';

/**
 * Слова, которые не остаются в конце строки одни.
 *
 * Короткие (одна-две буквы) ловятся длиной — их незачем перечислять. В
 * списке только те, что длиннее и всё равно тянут за собой следующее
 * слово: предлоги, союзы и вопросительные слова, после которых обрыв
 * строки читается как запинка.
 */
const CLINGY = [
  'без',
  'близ',
  'вне',
  'для',
  'если',
  'над',
  'надо',
  'около',
  'перед',
  'под',
  'подо',
  'после',
  'при',
  'про',
  'сквозь',
  'среди',
  'через',
  'что',
  'чтобы',
  'как',
  'когда',
  'пока',
  'хотя',
  'либо',
  'или',
  'ведь',
  'даже',
  'уже',
  'чем',
  'этот',
  'эти',
  'вместо',
  'кроме',
  'между',
];

const CLINGY_RE = new RegExp(`(^|[\\s(«„])(${CLINGY.join('|')}) `, 'gi');

/** Слово, к которому короткое может прилипнуть: буква, цифра или кавычка. */
const WORD_START = '[«„(0-9A-Za-zА-Яа-яЁё]';

export interface TypographOptions {
  /**
   * Связывать ли последние два слова (правило 5).
   *
   * Выключается для крупных заголовков. Правило про абзац: там строка
   * длинная, и одинокое слово на последней строке читается как обрыв. В
   * заголовке кегля 44px неразрывная пара — это готовое переполнение: на
   * узкой колонке «рекламного\u00A0кабинета» шире её самой, перенести
   * нельзя, и вбок уезжает вся страница.
   */
  tieLastWords?: boolean;
}

export function typograph(text: string, {tieLastWords = true}: TypographOptions = {}): string {
  let out = text;

  // 1. Число не рвётся: ни между разрядами, ни перед своей единицей.
  out = out.replace(/(\d) (?=\d)/g, `$1${NBSP}`);
  out = out.replace(/(\d) (?=[₽$€%])/g, `$1${NBSP}`);

  // 2. Короткое слово (одна-две буквы) уходит на следующую строку вместе
  //    со своим: «в 2026», «и пароль», «по емейлу».
  out = out.replace(
    new RegExp(`(^|[\\s(«„])([A-Za-zА-Яа-яЁё]{1,2}) (?=${WORD_START})`, 'g'),
    `$1$2${NBSP}`,
  );
  //    …и то же для слов подлиннее, которые всё равно не живут в конце строки.
  out = out.replace(CLINGY_RE, `$1$2${NBSP}`);

  // 3. Составное слово не переносится по дефису: «интернет-магазин»,
  //    «Санкт-Петербург». Дефис остаётся видимым, клей — нет.
  out = out.replace(/([A-Za-zА-Яа-яЁё0-9])-(?=[A-Za-zА-Яа-яЁё0-9])/g, `$1${WJ}-${WJ}`);

  // 4. Длинное тире не начинает строку. Пробел после него остаётся
  //    обычным — по нему перенос как раз осмыслен.
  out = out.replace(/ —/g, `${NBSP}—`);

  // 5. Последнее слово не висит одно — в тексте длиннее двух слов.
  //
  //    Склеиваем последний ОБЫЧНЫЙ пробел: если хвост уже сшит правилами
  //    выше («…на телефон»), делать нечего. Иначе второй проход по тому же
  //    тексту цеплял бы следующее слово с конца, и типограф переставал бы
  //    быть идемпотентным.
  //    Длинную пару не связываем совсем. Правило спасает ОДИНОКОЕ слово, а
  //    не любую пару: «срочного вмешательства» — это 22 знака, и на кегле
  //    вердикта такая связка шире колонки телефона, перенести её нельзя, и
  //    вбок уезжает весь документ. Порог взят по самой узкой колонке отчёта:
  //    в неё помещается около 28 знаков основного текста, и половина этого
  //    запаса — безопасная связка на любом кегле, кроме заголовочного.
  const words = out.split(/[\s\u00A0]+/).filter(Boolean);
  const lastPair = words.slice(-2).join(' ');
  if (tieLastWords && words.length > 2 && lastPair.length <= LAST_PAIR_MAX) {
    out = out.replace(/ (\S+)$/, `${NBSP}$1`);
  }

  return out;
}

/**
 * Обратная операция: убрать служебные символы.
 *
 * Нужна там, где текст покидает страницу, — в буфере обмена. Задачи
 * копируют в трекер или в письмо, а невидимый клей внутри слова ломает там
 * поиск по строке: «Санкт-Петербург» перестаёт находиться по самому себе.
 */
export function stripTypography(text: string): string {
  return text.replace(new RegExp(NBSP, 'g'), ' ').replace(new RegExp(WJ, 'g'), '');
}

/**
 * Ключи, значения которых текстом не являются: адреса, идентификаторы,
 * даты и коды перечислений. Неразрывный пробел в ISO-дате или в URL — это
 * не типографика, а испорченное значение.
 */
const VERBATIM_KEYS = new Set([
  // идентификаторы и ссылки
  'id',
  'campaignId',
  'key',
  'productUrl',
  'telegramUrl',
  // даты
  'date',
  'periodStart',
  'periodEnd',
  'nextReportDate',
  'lastSeenOn',
  'from',
  'to',
  // коды перечислений
  'adSystem',
  'anchor',
  'severity',
  'color',
  'scoreZone',
  'unit',
  'bound',
  'metric',
  'kind',
  'category',
  'quality',
  'priority',
  'planSource',
  'status',
  'mediaplanType',
]);

function walk(value: unknown, key?: string): unknown {
  if (typeof value === 'string') {
    return key !== undefined && VERBATIM_KEYS.has(key) ? value : typograph(value);
  }
  if (Array.isArray(value)) {
    // Элементы массива наследуют ключ самого массива: `campaigns: ['…']`
    // это по-прежнему кампании, а не безымянные строки.
    return value.map(item => walk(item, key));
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [childKey, walk(childValue, childKey)]),
    );
  }
  return value;
}

/**
 * Прогоняет весь отчёт через типограф разом.
 *
 * Одной точкой, а не вызовом в каждом месте вывода: текстовых полей в
 * отчёте под три десятка, и правило «не забыть обернуть» не переживёт
 * первую же новую секцию. Значения-не-текст (даты, ссылки, коды) остаются
 * нетронутыми — их список выше.
 */
export function typographReport(data: ReportData): ReportData {
  return walk(data) as ReportData;
}
